'use client'

import { Check, ChevronsUpDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { HelmServiceType } from '@/lib/api/types'
import { cn } from '@/lib/utils'

const serviceTypeOptions: HelmServiceType[] = [
  'PRODUCT_SOURCING',
  'SHIPPING_ONLY',
  'PVOC_TESTING',
  'CUSTOMS_CLEARANCE',
  'END_TO_END_IMPORT',
]

function formatServiceType(value: HelmServiceType) {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function ServiceTypeMultiSelect({
  value,
  onValueChange,
  disabled = false,
  id,
}: {
  value: HelmServiceType[]
  onValueChange: (value: HelmServiceType[]) => void
  disabled?: boolean
  id?: string
}) {
  function toggle(option: HelmServiceType) {
    if (value.includes(option)) {
      if (value.length === 1) return
      onValueChange(value.filter((selected) => selected !== option))
      return
    }

    onValueChange([...value, option])
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="field"
          role="combobox"
          aria-label="Select service types"
          aria-haspopup="listbox"
          disabled={disabled}
          className="h-auto min-h-10 w-full justify-between gap-2 py-2"
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            {value.map((option) => (
              <Badge key={option} variant="secondary">
                {formatServiceType(option)}
              </Badge>
            ))}
          </span>
          <ChevronsUpDown className="shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-1">
        <div role="listbox" aria-multiselectable="true" className="flex flex-col gap-0.5">
          {serviceTypeOptions.map((option) => {
            const selected = value.includes(option)
            const lastSelected = selected && value.length === 1

            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={disabled || lastSelected}
                onClick={() => toggle(option)}
                className={cn(
                  'flex min-h-9 w-full items-center gap-2 rounded-sm px-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                    selected && 'border-primary bg-primary text-primary-foreground',
                  )}
                >
                  {selected && <Check className="size-3" />}
                </span>
                <span className="flex-1">{formatServiceType(option)}</span>
              </button>
            )
          })}
        </div>
        <p className="px-2 py-1.5 text-xs text-muted-foreground">
          Select every service included in this consignment.
        </p>
      </PopoverContent>
    </Popover>
  )
}

export { formatServiceType, serviceTypeOptions }
